import { defineRetroArcadePlayerElement, type RetroArcadePlayerElement } from '@retro-arcade/embed';

import './styles.css';

const defaultPlayerOrigin = import.meta.env.VITE_PLAYER_ORIGIN?.trim() || 'http://127.0.0.1:5174';

const player = document.querySelector('#retro-player') as RetroArcadePlayerElement;
const playerOriginInput = document.querySelector('#player-origin') as HTMLInputElement;
const gameInput = document.querySelector('#game-identity') as HTMLInputElement;
const volumeInput = document.querySelector('#volume') as HTMLInputElement;
const volumeValue = document.querySelector('#volume-value') as HTMLOutputElement;
const connectionState = document.querySelector('#connection-state') as HTMLElement;
const eventLog = document.querySelector('#event-log') as HTMLOListElement;

const commandButtons = [
  document.querySelector('#load-game'),
  document.querySelector('#pause'),
  document.querySelector('#resume'),
  document.querySelector('#restart'),
  document.querySelector('#fullscreen'),
] as HTMLButtonElement[];

playerOriginInput.value = defaultPlayerOrigin;
player.setAttribute('player-origin', defaultPlayerOrigin);

const initialGame = new URL(window.location.href).searchParams.get('game')?.trim();
if (initialGame) {
  gameInput.value = initialGame;
  player.setAttribute('game', initialGame);
}

defineRetroArcadePlayerElement();

function setControlsEnabled(enabled: boolean) {
  for (const button of commandButtons) button.disabled = !enabled;
  volumeInput.disabled = !enabled;
}

function appendEvent(type: string, detail: unknown) {
  const item = document.createElement('li');
  const timestamp = new Date().toLocaleTimeString();
  item.textContent = `${timestamp} · ${type} · ${JSON.stringify(detail)}`;
  eventLog.prepend(item);
}

async function runHostCommand(label: string, action: () => Promise<void>) {
  try {
    await action();
    appendEvent('HOST_COMMAND_OK', { command: label });
  } catch (error) {
    appendEvent('HOST_COMMAND_ERROR', {
      command: label,
      message: error instanceof Error ? error.message : 'Unknown host command failure.',
    });
  }
}

player.addEventListener('retro-arcade-ready', (event) => {
  connectionState.textContent = 'ready';
  connectionState.dataset.state = 'ready';
  setControlsEnabled(true);
  appendEvent('PLAYER_READY', (event as CustomEvent<unknown>).detail);
});

player.addEventListener('retro-arcade-game-changed', (event) => {
  appendEvent('GAME_CHANGED', (event as CustomEvent<unknown>).detail);
});

player.addEventListener('retro-arcade-runtime-state', (event) => {
  appendEvent('RUNTIME_STATE', (event as CustomEvent<unknown>).detail);
});

player.addEventListener('retro-arcade-error', (event) => {
  connectionState.textContent = 'error';
  connectionState.dataset.state = 'error';
  appendEvent('PLAYER_ERROR', (event as CustomEvent<unknown>).detail);
});

document.querySelector('#remount')?.addEventListener('click', () => {
  const origin = playerOriginInput.value.trim();
  if (!origin) return;
  connectionState.textContent = 'remounting';
  connectionState.dataset.state = 'waiting';
  setControlsEnabled(false);
  player.setAttribute('player-origin', origin);
});

document.querySelector('#load-game')?.addEventListener('click', () => {
  const game = gameInput.value.trim();
  if (!game) return;
  void runHostCommand('LOAD_GAME', () => player.loadGame(game));
});

document.querySelector('#pause')?.addEventListener('click', () => {
  void runHostCommand('PAUSE', () => player.pause());
});

document.querySelector('#resume')?.addEventListener('click', () => {
  void runHostCommand('RESUME', () => player.resume());
});

document.querySelector('#restart')?.addEventListener('click', () => {
  void runHostCommand('RESTART', () => player.restart());
});

document.querySelector('#fullscreen')?.addEventListener('click', () => {
  void runHostCommand('REQUEST_FULLSCREEN', () => player.requestGameFullscreen());
});

volumeInput.addEventListener('input', () => {
  volumeValue.value = `${Math.round(Number(volumeInput.value) * 100)}%`;
});

volumeInput.addEventListener('change', () => {
  const volume = Number(volumeInput.value);
  void runHostCommand('SET_VOLUME', () => player.setVolume(volume));
});

document.querySelector('#clear-events')?.addEventListener('click', () => {
  eventLog.replaceChildren();
});
